import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { fcfa } from "@/lib/format";
import { Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { Stars } from "@/components/ProductReviews";
import { catSlug } from "@/lib/categories";
import HeroCarousel from "@/components/HeroCarousel";
import { useSeo } from "@/lib/seo";

const Home = () => {
  useSeo({
    title: "Boutique maison & salle de bain à Dakar",
    description:
      "Sama-Keur : robinetterie, meubles de salle de bain, lits coffres, électricité et décoration à Dakar, Sénégal. Livraison rapide à Dakar et en région. Paiement Wave, Orange Money et carte bancaire.",
    keywords:
      "meubles Dakar, salle de bain moderne Dakar, chambre à coucher Sénégal, cuisine équipée Sénégal, showroom Dakar, lit coffre Dakar, livraison Sénégal",
    path: "/",
  });
  const [cfg, setCfg] = useState(null);
  const [products, setProducts] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [cats, setCats] = useState([]);
  const [zones, setZones] = useState([]);
  const manifestoRef = useRef(null);
  const { scrollYProgress: manifestoProgress } = useScroll({ target: manifestoRef, offset: ["start end", "end start"] });
  const manifestoY = useTransform(manifestoProgress, [0, 1], ["-8%", "8%"]);

  useEffect(() => {
    api.get("/settings/home").then((r) => setCfg(r.data)).catch(() => setCfg({}));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/reviews/featured").then((r) => setFeaturedReviews(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {});
    api.get("/delivery-options").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  if (!cfg) {
    return (
      <main data-testid="home-page" className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-pulse rounded-full bg-clay" />
      </main>
    );
  }

  const { hero = {}, featured_products: fp = {}, featured_categories: fc = {}, manifesto = {}, quote_band: qb = {}, reviews: rv = {} } = cfg;
  const featuredItems = (fp.product_ids || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const anySection = hero.enabled || fp.enabled || fc.enabled || manifesto.enabled || qb.enabled || (rv.enabled && featuredReviews.length > 0);

  return (
    <main data-testid="home-page">
      {/* CARROUSEL PRODUITS VEDETTES — défilement automatique toutes les 3 s */}
      <HeroCarousel hero={hero} slides={featuredItems.length ? featuredItems : products.slice(0, 5)} />

      {/* ONGLETS CATÉGORIES — juste sous le carrousel */}
      {cats.length > 0 && (
        <nav data-testid="home-category-tabs" className="border-b border-clay bg-cream">
          <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-6 py-4 lg:justify-center lg:px-10">
            {cats.map((c) => (
              <Link
                key={c}
                to={`/boutique?categorie=${encodeURIComponent(c)}`}
                data-testid={`home-cat-tab-${catSlug(c)}`}
                className="shrink-0 rounded-full border border-clay bg-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-bark transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
              >
                {c}
              </Link>
            ))}
          </div>
        </nav>
      )}

      <Marquee />

      {/* CATEGORIES MISES EN AVANT */}
      {fc.enabled && (fc.items || []).length > 0 && (
        <section data-testid="home-categories" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Nos univers</p>
              <h2 data-testid="home-categories-title" className="mt-3 font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl md:text-5xl">
                {fc.title || "Nos univers"}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/boutique" data-testid="categories-see-all" className="group flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:text-terracotta">
                Tout le catalogue
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="grid auto-rows-[240px] grid-cols-1 gap-4 md:grid-cols-3">
            {fc.items.map((cat, i) => (
              <Reveal key={`${cat.name}-${i}`} delay={i * 0.1} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
                <Link
                  to={`/boutique?categorie=${encodeURIComponent(cat.name)}`}
                  data-testid={`category-card-${catSlug(cat.name || "item")}`}
                  className="group relative block h-full w-full overflow-hidden border border-clay bg-clay/30"
                >
                  {cat.image && (
                    <img
                      src={cat.image}
                      alt={cat.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-bark/80 via-bark/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <p className="font-display text-2xl font-semibold text-sand md:text-3xl">{cat.name}</p>
                    {cat.desc && <p className="mt-1 text-sm text-sand/70">{cat.desc}</p>}
                  </div>
                  <span className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-sand/15 text-sand backdrop-blur transition-colors duration-300 group-hover:bg-terracotta">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* PRODUITS MIS EN AVANT */}
      {fp.enabled && featuredItems.length > 0 && (
        <section data-testid="home-featured" className="border-y border-clay bg-cream">
          <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
            <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
              <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Sélection du moment</p>
                <h2 data-testid="home-featured-title" className="mt-3 font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl md:text-5xl">
                  {fp.title || "Nos pièces du moment"}
                </h2>
              </Reveal>
            </div>
            <div data-testid="featured-products-grid" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {featuredItems.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
            <Reveal className="mt-12 text-center">
              <Link
                to="/boutique"
                data-testid="featured-see-all-button"
                className="inline-flex h-14 items-center gap-3 rounded-full border border-bark/20 px-8 text-sm font-bold uppercase tracking-[0.15em] text-bark transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
              >
                Voir toute la boutique
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* MANIFESTE */}
      {manifesto.enabled && (
        <section ref={manifestoRef} data-testid="manifesto-section" className="grain relative overflow-hidden bg-bark py-28 text-sand lg:py-40">
          <div className="relative z-[2] mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ochre">Notre manifeste</p>
                {manifesto.title && (
                  <h2 data-testid="manifesto-title" className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                    {manifesto.title}
                  </h2>
                )}
                {manifesto.text && (
                  <p data-testid="manifesto-text" className="mt-6 text-base leading-relaxed text-sand/60">
                    {manifesto.text}
                  </p>
                )}
              </Reveal>
              {manifesto.image && (
                <Reveal delay={0.2} className="mt-10 overflow-hidden border border-sand/15">
                  <motion.img
                    src={manifesto.image}
                    alt={manifesto.title || "Manifeste Sama-Keur"}
                    data-testid="manifesto-image"
                    style={{ y: manifestoY, scale: 1.15 }}
                    className="aspect-[4/3] w-full object-cover"
                  />
                </Reveal>
              )}
            </div>
            <div className="flex flex-col justify-center gap-2 lg:col-span-7">
              {(manifesto.chapters || []).map((c, i) => (
                <Reveal key={i} delay={i * 0.12}>
                  <div data-testid={`manifesto-chapter-${i}`} className="group flex gap-8 border-b border-sand/10 py-10 transition-colors duration-500 hover:border-ochre/40">
                    <span className="font-display text-4xl font-bold text-ochre/50 transition-colors duration-500 group-hover:text-ochre md:text-5xl">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-display text-2xl font-medium tracking-tight md:text-3xl">{c.title}</h3>
                      <p className="mt-3 max-w-lg text-sm leading-relaxed text-sand/55 md:text-base">{c.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BANDEAU CITATION */}
      {qb.enabled && (
        <section data-testid="home-quote-band" className="relative overflow-hidden">
          {qb.image && <img src={qb.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />}
          {qb.image && <div className="absolute inset-0 bg-bark/55" />}
          <div className={`relative flex items-center py-24 lg:py-32 ${!qb.image ? "bg-clay/30" : ""}`}>
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
              <Reveal>
                <p data-testid="quote-band-text" className={`max-w-2xl font-display text-3xl font-semibold leading-snug md:text-5xl ${qb.image ? "text-sand" : "text-bark"}`}>
                  {qb.text}
                </p>
                {qb.author && (
                  <p data-testid="quote-band-author" className={`mt-6 text-xs font-semibold uppercase tracking-[0.3em] ${qb.image ? "text-ochre" : "text-terracotta"}`}>
                    — {qb.author}
                  </p>
                )}
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* LIVRAISON AU SÉNÉGAL */}
      {zones.length > 0 && (
        <section data-testid="home-delivery" className="border-y border-clay bg-cream">
          <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
            <Reveal>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Livraison au Sénégal</p>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl">
                Nous livrons partout, de Dakar à Saint-Louis
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-bark/60">
                Commandez en ligne, payez par Wave, Orange Money ou carte bancaire — nous livrons votre commande
                à domicile à Dakar et sa banlieue, à Thiès, Mbour, Saint-Louis et dans les autres villes du Sénégal.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {zones.map((z) => (
                <Reveal key={z.id}>
                  <div data-testid={`delivery-zone-${z.id}`} className="flex h-full items-start justify-between gap-4 border border-clay bg-white p-6 transition-colors duration-300 hover:border-terracotta">
                    <div>
                      <p className="font-display text-lg font-semibold text-bark">{z.name}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.15em] text-bark/45">{z.city} — {z.delay}</p>
                    </div>
                    <p className="shrink-0 font-display text-lg font-bold text-terracotta">{fcfa(z.price)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AVIS CLIENTS EN VEDETTE */}
      {rv.enabled && featuredReviews.length > 0 && (
        <section data-testid="home-reviews" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Avis clients</p>
            <h2 data-testid="home-reviews-title" className="mt-3 font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl md:text-5xl">
              {rv.title || "Ce que Dakar dit de nous"}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {featuredReviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.12}>
                <div data-testid={`home-review-${i}`} className="flex h-full flex-col border border-clay bg-white p-8">
                  <Stars value={r.rating} size="h-5 w-5" />
                  <p className="mt-5 flex-1 font-display text-lg font-medium leading-relaxed text-bark">« {r.comment} »</p>
                  <div className="mt-6 border-t border-clay pt-4">
                    <p className="text-sm font-semibold text-bark" data-testid={`home-review-author-${i}`}>{r.author_name}</p>
                    {r.product_name && (
                      <Link to={`/produit/${r.product_id}`} className="mt-0.5 block text-xs text-bark/50 transition-colors hover:text-terracotta">
                        {r.product_name}
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ACCUEIL VIDE */}
      {!anySection && (
        <section data-testid="home-empty" className="mx-auto flex max-w-7xl flex-col items-center px-6 py-32 text-center lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Sama-Keur — Dakar, Sénégal</p>
          <h1 className="mt-6 font-display text-4xl font-bold tracking-tighter text-bark sm:text-5xl md:text-6xl">
            Bienvenue chez <span className="italic text-terracotta">Sama-Keur</span>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-bark/60">
            Notre page d'accueil est en cours de préparation. La boutique, elle, est déjà ouverte.
          </p>
          <Link
            to="/boutique"
            data-testid="home-empty-cta"
            className="group mt-10 flex h-14 items-center gap-3 rounded-full bg-terracotta px-8 text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark"
          >
            Découvrir la boutique
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </section>
      )}
    </main>
  );
};

export default Home;
